import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import { global_initial } from "./constants/global_initial";

/**
 * The GlobalContext is the context object created using createContext().
 * It serves as the global store for sharing and accessing data across components.
 *
 * @typedef {Object} GlobalContext
 */
const GlobalContext = createContext();

/**
 * GlobalProvider is a React component that provides a global context for the application.
 * It stores and manages the state of various variables used throughout the app.
 *
 * @param {Object} children - The child components to be rendered inside the GlobalProvider.
 *
 * @return {React.Element} - The JSX element containing the child components wrapped in the GlobalContext.Provider.
 */
function GlobalProvider({ children }) {
  const [CURRENT_STREAM, SET_CURRENT_STREAM] = useState(101);
  const [SCREEN_STATE, SET_SCREEN_STATE] = useState(global_initial.SCREEN_STATE);
  const [MEDIA_TRACKS, SET_MEDIA_TRACKS] = useState(global_initial.MEDIA_TRACKS);
  const [MEDIA_TRACK_IDS, SET_MEDIA_TRACK_IDS] = useState(global_initial.MEDIA_TRACK_IDS);
  const [MAIN_SCREEN_STREAM_LOADED, SET_MAIN_SCREEN_STREAM_LOADED] = useState(false);
  const [SCREEN_SWITCH_DEBUG_MODE, SET_SCREEN_SWITCH_DEBUG_MODE] = useState(false);
  const [SCREEN_SWITCH_DEBUG_PANEL, SET_SCREEN_SWITCH_DEBUG_PANEL] = useState(false);
  const [SCREEN_SWITCH_PROGGRESS, SET_SCREEN_SWITCH_PROGGRESS] = useState(0);
  const [SCREEN_IS_MAIN_FG, SET_SCREEN_IS_MAIN_FG] = useState(true);
  const [MEETING_STATE, SET_MEETING_STATE] = useState(global_initial.MEETING_STATE);
  const [SCREEN_SWITCH_STEP, SET_SCREEN_SWITCH_STEP] = useState(global_initial.SCREEN_SWITCH_STEP);
  const [MINI_TRACKS, SET_MINI_TRACKS] = useState(global_initial.MINI_TRACKS);
  const JANUS_REF = useRef({ instance: null, plugin: { streaming: null, videoRoom: null } });
  const MEETING_REF = useRef(global_initial.MEETING_STATE);
  const SCREEN_REF = useRef(global_initial.SCREEN_STATE);
  const [AUTO_JOIN, SET_AUTO_JOIN] = useState(global_initial.AUTO_JOIN);
  const [GRID_STATE, SET_GRID_STATE] = useState(global_initial.GRID_STATE);

  const PLAYER_REF = useRef(null);
  const SCREEN_STREAM_MAIN_REF = useRef(new MediaStream());
  const SCREEN_STREAM_TEMP_REF = useRef(new MediaStream());
  const SCREEN_STREAM_MINI_REF = useRef(new MediaStream());
  const SCREEN_STREAM_AUDIO_REF = useRef(new MediaStream());
  const SCREEN_SOUND__REF = useRef(new MediaStream());
  const [RESET, RESET_GLOBAL_CONTEXT] = useState(false);

  const VIDEO_MAIN_PARENT = useRef(null);

  const VIDEO_MAIN = useRef(null);
  const VIDEO_TEMP = useRef(null);
  const VIDEO_MINI = useRef(null);
  const MEETING_STATUSBAR_VIDEO_MAIN_PREVIEW = useRef(null);
  const LAST_FRAME_IMG = useRef(null);
  const AUDIO_SCREEN_SOUND = useRef(null);
  const VIDEO_MAIN_HIDDEN = useRef(null);
  const VIDEO_TEMP_HIDDEN = useRef(null);
  const AUDIO_SCREEN = useRef(null);
  const SPAN_SIZE_MAIN = useRef(null);
  const SPAN_SIZE_TEMP = useRef(null);
  const DEBUG_MINI_VIDEO_101 = useRef(null);

  const reset = () => {
    SET_CURRENT_STREAM(101);
    SET_SCREEN_STATE(global_initial.SCREEN_STATE);
    SET_MEDIA_TRACKS(global_initial.MEDIA_TRACKS);
    SET_MEDIA_TRACK_IDS(global_initial.MEDIA_TRACK_IDS);
    SET_SCREEN_SWITCH_STEP(global_initial.SCREEN_SWITCH_STEP);
    SET_SCREEN_SWITCH_DEBUG_MODE(false);
    SET_SCREEN_SWITCH_DEBUG_PANEL(false);
    SET_SCREEN_SWITCH_PROGGRESS(0);
    SET_SCREEN_IS_MAIN_FG(true);
    SET_MEETING_STATE(global_initial.MEETING_STATE);
    SET_AUTO_JOIN(global_initial.AUTO_JOIN);
    RESET_GLOBAL_CONTEXT(false);
    JANUS_REF.current = { instance: null, plugin: { streaming: null, videoRoom: null } };
    MEETING_REF.current = global_initial.MEETING_STATE;
    SCREEN_REF.current = global_initial.SCREEN_STATE;
  };

  useEffect(() => console.log(SCREEN_STATE.currentScreen), [SCREEN_STATE.currentScreen]);

  useEffect(() => {
    if (RESET) {
      console.log("RESETTTTTTTTTTTTT");
      reset();
    }
  }, [RESET]);

  const value = useMemo(
    () => ({
      CURRENT_STREAM,
      SET_CURRENT_STREAM,
      SCREEN_STATE,
      SET_SCREEN_STATE,
      MEDIA_TRACKS,
      SET_MEDIA_TRACKS,
      MEDIA_TRACK_IDS,
      SET_MEDIA_TRACK_IDS,
      MAIN_SCREEN_STREAM_LOADED,
      SET_MAIN_SCREEN_STREAM_LOADED,
      SCREEN_SWITCH_DEBUG_MODE,
      SET_SCREEN_SWITCH_DEBUG_MODE,
      SCREEN_SWITCH_DEBUG_PANEL,
      SET_SCREEN_SWITCH_DEBUG_PANEL,
      SCREEN_SWITCH_PROGGRESS,
      SET_SCREEN_SWITCH_PROGGRESS,
      SCREEN_IS_MAIN_FG,
      SET_SCREEN_IS_MAIN_FG,
      MEETING_STATE,
      SET_MEETING_STATE,
      SCREEN_SWITCH_STEP,
      SET_SCREEN_SWITCH_STEP,
      MINI_TRACKS,
      SET_MINI_TRACKS,
      JANUS_REF,
      MEETING_REF,
      SCREEN_REF,
      AUTO_JOIN,
      SET_AUTO_JOIN,
      RESET_GLOBAL_CONTEXT,
      SCREEN_STREAM_MAIN_REF,
      SCREEN_STREAM_TEMP_REF,
      SCREEN_STREAM_MINI_REF,
      SCREEN_STREAM_AUDIO_REF,
      SCREEN_SOUND__REF,
      GRID_STATE,
      SET_GRID_STATE,
      PLAYER_REF,
      VIDEO_MAIN_PARENT,
      VIDEO_MAIN,
      VIDEO_TEMP,
      VIDEO_MINI,
      MEETING_STATUSBAR_VIDEO_MAIN_PREVIEW,
      LAST_FRAME_IMG,
      AUDIO_SCREEN_SOUND,
      VIDEO_MAIN_HIDDEN,
      VIDEO_TEMP_HIDDEN,
      AUDIO_SCREEN,
      SPAN_SIZE_MAIN,
      SPAN_SIZE_TEMP,
      DEBUG_MINI_VIDEO_101,
    }),
    [
      MINI_TRACKS,
      CURRENT_STREAM,
      SCREEN_STATE,
      MEDIA_TRACKS,
      MEDIA_TRACK_IDS,
      MAIN_SCREEN_STREAM_LOADED,
      SCREEN_SWITCH_DEBUG_MODE,
      SCREEN_SWITCH_DEBUG_PANEL,
      SCREEN_SWITCH_PROGGRESS,
      SCREEN_IS_MAIN_FG,
      MEETING_STATE,
      SCREEN_SWITCH_STEP,
      AUTO_JOIN,
      GRID_STATE,
    ],
  );

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
}

/**
 * Retrieves the global context from the GlobalProvider.
 *
 * @throws {Error} Throws an error if used outside of the GlobalProvider.
 *
 * @returns {*} The global context value.
 */
const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (context === undefined) throw new Error("useGlobal must be used within a GlobalProvider");
  return context;
};

export { GlobalProvider, useGlobal };
